import { __assign, __awaiter, __generator } from "tslib";
import * as _ from 'lodash';
import { keepSupportedCeTypes } from './services/keepSupportedCeTypes';
import { replaceWithNewKitIds } from './services/replaceWithNewKitIds';
import { filterMediaByProfileId } from './services/filterMediaByProfileId';
import { DEFAULT_CATEGORY } from './constants/defaultCategory';
import { DEFAULT_CONTENT } from './constants/defaultContentData';
import { defaultReplacementText } from './constants/defaultReplacementText';
import { ContentMapCreator } from './contentMapCreator';
import { ContentItemsGenerator } from './contentItemGenerator';
import { generateContentId } from './contentId';
import { getAppsRecommendation } from './appsRecommendation';
import { getRequest } from './services/request';
import { getBaseUrl } from './utils/url';
import { InvalidArgumentsError, NotInitializedError } from './errors';
import { setFlags } from './services/features';
import { getApps } from './apps';
import { getProfileIds } from './services/getProfileIds';
var DEFAULT_LOCALE = 'en';
var DEFAULT_GEO = 'all';
var DEFAULT_CONTENT_API_OPTIONS = {
    locale: DEFAULT_LOCALE,
    geo: DEFAULT_GEO,
    flags: {},
};
var API_PREFIX_CONTENT_GENERATION = '/_api/funnel-content-generation-service';
var API_PREFIX_TAXONOMY = '/_api/funnel-taxonomy-service';
var ContentManager = /** @class */ (function () {
    function ContentManager(options) {
        this.aliasToTypeMap = {}; // TODO add typings
        this.replacements = {}; // TODO add typings
        this.flags = {};
        this.isDefaultContent = false;
        if (!options.origin) {
            throw new InvalidArgumentsError('Invalid argument: origin can not be empty!');
        }
        this.baseUrl = getBaseUrl(options.baseUrl);
        this.options = __assign(__assign({}, DEFAULT_CONTENT_API_OPTIONS), options);
        if (options.flags) {
            this.flags = this.options.flags;
            setFlags(this.options.flags);
        }
        this.contentMapCreator = new ContentMapCreator(this.options.flags);
        if (options.supportedCeTypes) {
            this.supportedCeTypes = new Set(options.supportedCeTypes);
        }
    }
    /* In the content-manager we use this method to pass updated replacements */
    ContentManager.prototype.setReplacements = function (replacements) {
        if (replacements === void 0) { replacements = {}; }
        this.replacements = replacements;
        this.initContentMap();
        if (this.contentMap && this.contentItemGenerator) {
            this.contentItemGenerator.updateReplacements(this.contentMap, this.contentMapCreator.replacementsWithPlaceholders, this.contentMapCreator.replacementsWithDefaults);
        }
    };
    ContentManager.prototype.setGeo = function (countryCode) {
        this.options.geo = countryCode;
        // if content already initialised reinit it again
        if (this.content) {
            this.initContentMap();
            this.initContentElementGenerator();
        }
    };
    ContentManager.prototype.setProfileId = function (profileId) {
        this.options.profileId = profileId;
        // if content already initialised reinit it again
        if (this.content) {
            this.initContentMap();
            this.initContentElementGenerator();
        }
    };
    /**
     * @deprecated
     * BusinessTerms (aka Categories) are moved to funnel-taxonomy-service and this method is deprecated.
     * Please make sure to use BusinessTermService Search method instead.
     *
     * Migration guide:
     * https://docs.google.com/document/d/1my-dQEJIps4fn-q2tLOpMpuEbNboHWFH3boNIWkcYPE/edit#heading=h.3d349ks0iu31
     **/
    ContentManager.prototype.suggest = function (value, maxResults, maxRelatedResults) {
        var _this = this;
        var url = this.baseUrl + API_PREFIX_TAXONOMY + '/v1/business-term/search';
        var params = {
            q: value,
            source: this.options.origin,
        };
        if (this.options.locale !== DEFAULT_LOCALE) {
            params.locale = this.options.locale;
        }
        if (this.options.editorSessionId) {
            params.editorSessionId = this.options.editorSessionId;
        }
        if (maxResults) {
            params.limit = maxResults;
        }
        if (maxRelatedResults) {
            params.relatedLimit = maxRelatedResults;
        }
        return getRequest({ url: url, params: params }).then(function (response) {
            var _a, _b, _c;
            return (__assign(__assign({}, response.data), { relatedResults: (_a = response.data.relatedResults) !== null && _a !== void 0 ? _a : [], locale: (_b = response.data.locale) !== null && _b !== void 0 ? _b : _this.options.locale, offset: (_c = response.data.offset) !== null && _c !== void 0 ? _c : 0 }));
        });
    };
    /*
      Load and initialize content by industry and structure id
     */
    ContentManager.prototype.initContent = function (industryId, structureId, replacements, useDefaultCategory, useDefaultContent) {
        if (useDefaultCategory === void 0) { useDefaultCategory = true; }
        if (useDefaultContent === void 0) { useDefaultContent = true; }
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                if (!industryId || !structureId) {
                    return [2 /*return*/, Promise.reject(new InvalidArgumentsError('Invalid arguments: industryId or structureId is missing'))];
                }
                return [2 /*return*/, this.loadContent(industryId, structureId)
                        .catch(function (err) {
                        if (useDefaultCategory) {
                            return _this.loadContent(DEFAULT_CATEGORY.industryId, DEFAULT_CATEGORY.structureId);
                        }
                        return Promise.reject(err);
                    })
                        .catch(function (err) {
                        if (useDefaultContent) {
                            _this.isDefaultContent = true;
                            return Promise.resolve(DEFAULT_CONTENT);
                        }
                        return Promise.reject(err);
                    })
                        .then(function (content) { return __awaiter(_this, void 0, void 0, function () {
                        return __generator(this, function (_a) {
                            this.industryId = industryId;
                            this.structureId = structureId;
                            this.replacements = replacements || {};
                            return [2 /*return*/, this.handleContent(content)];
                        });
                    }); })];
            });
        });
    };
    ContentManager.prototype.initContentStatic = function (data) {
        return __awaiter(this, void 0, void 0, function () {
            var industryId, structureId, replacements;
            return __generator(this, function (_a) {
                industryId = data.industryId, structureId = data.structureId, replacements = data.replacements;
                this.industryId = industryId;
                this.structureId = structureId;
                this.replacements = replacements || {};
                return [2 /*return*/, this.handleContent(data.content)];
            });
        });
    };
    /*
      Is default content used as a fallback or not
     */
    ContentManager.prototype.isDefaultContentUsed = function () {
        return this.isDefaultContent;
    };
    /*
      Returns a list of suggested pages
     */
    ContentManager.prototype.getPages = function () {
        this.throwIfNotInitialized();
        if (this.content) {
            return this.content.pages;
        }
    };
    ContentManager.prototype.getRawContent = function () {
        this.throwIfNotInitialized();
        return this.content;
    };
    ContentManager.prototype.getContentMap = function () {
        return this.contentMap;
    };
    /*
      Return a list of recommended kit IDs
     */
    ContentManager.prototype.getKitIds = function () {
        this.throwIfNotInitialized();
        return this.content && this.content.kits;
    };
    ContentManager.prototype.getLayoutFamilyBlacklist = function () {
        this.throwIfNotInitialized();
        return this.content && this.content.layoutFamilyBlacklist;
    };
    ContentManager.prototype.getProfileMapping = function () {
        this.throwIfNotInitialized();
        return this.content && this.content.profileMapping;
    };
    ContentManager.prototype.getProfileIds = function () {
        var _a;
        this.throwIfNotInitialized();
        return getProfileIds((_a = this.originalLoadedContent) === null || _a === void 0 ? void 0 : _a.contentItems);
    };
    ContentManager.prototype.getAliasByCeType = function (ceType) {
        this.throwIfNotInitialized();
        return (_.findKey(this.aliasToTypeMap, function (ceTypeLocal) { return ceTypeLocal === ceType; }) || null);
    };
    /*
      Returns an object with Metasite ID for each TPA type
     */
    ContentManager.prototype.getTpaMetasitesIDs = function () {
        this.throwIfNotInitialized();
        return (this.contentItemGenerator && this.contentItemGenerator.getTpaMetaSiteIds());
    };
    /*
      Generate a content item suggestion
     */
    ContentManager.prototype.generateContentItems = function (data, count, randomOffset) {
        var _a;
        if (count === void 0) { count = 1; }
        if (randomOffset === void 0) { randomOffset = 0; }
        this.throwIfNotInitialized();
        var id = generateContentId(data);
        return (_a = this.contentItemGenerator) === null || _a === void 0 ? void 0 : _a.generate(id, count, randomOffset);
    };
    /*
      Generate a list content item
     */
    ContentManager.prototype.generateListContentItems = function (data, count, cardinality, randomOffset) {
        var _a;
        if (count === void 0) { count = 1; }
        if (cardinality === void 0) { cardinality = 3; }
        if (randomOffset === void 0) { randomOffset = 0; }
        this.throwIfNotInitialized();
        var id = generateContentId(data);
        return (_a = this.contentItemGenerator) === null || _a === void 0 ? void 0 : _a.generateList(id, count, cardinality, randomOffset);
    };
    /*
      Generate a list of content text data
     */
    ContentManager.prototype.generateTextData = function () {
        var _a;
        this.throwIfNotInitialized();
        return (_a = this.contentItemGenerator) === null || _a === void 0 ? void 0 : _a.getContentTextData();
    };
    ContentManager.prototype.getAppsRecommendation = function () {
        this.throwIfNotInitialized();
        return getAppsRecommendation(this.content);
    };
    ContentManager.prototype.getApps = function () {
        this.throwIfNotInitialized();
        return getApps({
            content: this.content,
            structureId: this.structureId || '',
        }, this.flags);
    };
    ContentManager.prototype.handleContent = function (content) {
        this.originalLoadedContent = content;
        this.initContentMap();
        this.initContentElementGenerator();
        this.initAliasToTypeMap();
        // return all private data and reuse it inside ADIs contentManger
        // it's a temp solution
        return {
            content: this.content,
            contentMap: this.contentMap,
            aliasToTypeMap: this.aliasToTypeMap,
        };
    };
    ContentManager.prototype.throwIfNotInitialized = function () {
        if (!this.content) {
            throw new NotInitializedError();
        }
    };
    ContentManager.prototype.initAliasToTypeMap = function () {
        var _this = this;
        _.forEach(this.content && this.content.pages, function (page) {
            _.forEach(page.sections, function (section) {
                if (section.id && section.id !== section.ceType) {
                    _this.aliasToTypeMap[section.id] = section.ceType;
                }
            });
        });
    };
    ContentManager.prototype.initContentElementGenerator = function () {
        if (this.contentMap) {
            this.contentItemGenerator = new ContentItemsGenerator(this.contentMap, this.contentMapCreator.replacementsWithPlaceholders, this.contentMapCreator.replacementsWithDefaults, this.options.locale, this.options.geo);
        }
        else {
            throw new Error('Content Map should be initialised');
        }
    };
    ContentManager.prototype.initContentMap = function () {
        var _a;
        if (this.originalLoadedContent) {
            this.content = keepSupportedCeTypes(this.originalLoadedContent, this.supportedCeTypes);
            this.content.kits = replaceWithNewKitIds(this.content.kits);
            this.content.contentItems = filterMediaByProfileId((_a = this.content) === null || _a === void 0 ? void 0 : _a.contentItems, this.options.profileId);
        }
        var settings = {
            structureId: this.structureId,
            industryId: this.industryId,
            contentItems: this.content && this.content.contentItems,
            placeholders: this.content && this.content.placeholders,
            replacements: this.replacements,
            defaultReplacements: defaultReplacementText,
            locale: this.options.locale,
            geo: this.options.geo,
        };
        this.contentMap = this.contentMapCreator.createMap(settings);
    };
    ContentManager.prototype.loadContent = function (industryId, structureId) {
        return __awaiter(this, void 0, void 0, function () {
            var url, params;
            return __generator(this, function (_a) {
                url = this.baseUrl + API_PREFIX_CONTENT_GENERATION + '/v1/content/lookup';
                params = {
                    industryId: industryId,
                    structureId: structureId,
                };
                if (this.options.locale !== DEFAULT_LOCALE) {
                    params.locale = this.options.locale;
                }
                if (this.options.contentVersion) {
                    params.indexVersion = this.options.contentVersion;
                }
                return [2 /*return*/, getRequest({ url: url, params: params }).then(function (response) { return response.data; })];
            });
        });
    };
    return ContentManager;
}());
export { ContentManager };
export default ContentManager;
//# sourceMappingURL=contentApi.js.map